// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedicationStatement, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';

export interface UsePatientMedicationsResult {
  medications: MedicationStatement[];
  loading: boolean;
  error: Error | null;
  setMedications: (medications: MedicationStatement[]) => void;
  refresh: () => void;
}

export function usePatientMedications(patient: Patient | undefined): UsePatientMedicationsResult {
  const medplum = useMedplum();
  const [medications, setMedications] = useState<MedicationStatement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!patient?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const results = await medplum.searchResources('MedicationStatement', {
        subject: `Patient/${patient.id}`,
        _sort: '-_lastUpdated',
        _count: '100',
      });
      setMedications(Array.from(results) as MedicationStatement[]);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load medications'));
    } finally {
      setLoading(false);
    }
  }, [medplum, patient?.id]);

  useEffect(() => {
    fetch().catch(console.error);
  }, [fetch]);

  return { medications, loading, error, setMedications, refresh: fetch };
}
